// Live's capability narrowing for the video composer (G6 section 1, live's
// `video-form-capabilities-provider.tsx`, `capabilities-utils.ts`): a model's modes are its
// text, image and reference to video blocks, in that order. The filled inputs narrow them, each
// in turn, keeping the modes that take it (or all of them, when none does); the first mode left
// is the job's type. An input shows when any mode takes it, and is disabled when no mode left
// does.
import type {
  CatalogImageModel,
  CatalogVideoModel,
  GenerationType,
  VideoMode,
} from "../../../api/generated/model";

/** The video types, in live's order. */
export const VIDEO_TYPES: readonly GenerationType[] = ["video.text", "video.image", "video.reference"];

/** What the draft has filled, as the narrowing reads it. */
export interface Filled {
  start: boolean;
  end: boolean;
  /** How many keyframes between the frames (Kling 4.0's, ours: spec web/keyframes-slot). */
  middle: number;
  /** How many reference images, videos and audios. */
  references: number;
  videos: number;
  audios: number;
  /** How many elements. */
  elements: number;
  shots: boolean;
}

export type VideoInput =
  | "start"
  | "end"
  | "middle"
  | "references"
  | "videos"
  | "audios"
  | "elements"
  | "shots";

/** Live's field order for the narrowing (`CAPABILITY_FIELDS`), the keyframes after the frames. */
const ORDER: readonly VideoInput[] = [
  "start",
  "end",
  "middle",
  "references",
  "videos",
  "audios",
  "elements",
  "shots",
];

/** The inputs counted rather than set. */
type Counted = "middle" | "references" | "videos" | "audios" | "elements";
const COUNTED: readonly VideoInput[] = ["middle", "references", "videos", "audios", "elements"];

const taken = (block: VideoMode, input: VideoInput, filled: Filled): boolean => {
  switch (input) {
    case "start":
      return block.start_frame !== undefined && block.start_frame !== "none";
    case "end":
      return block.end_frame === true;
    case "middle":
      return (block.middle_frames?.max ?? 0) >= Math.max(1, filled.middle);
    case "references":
      return (block.reference_images?.max ?? 0) >= Math.max(1, filled.references);
    case "videos":
      return (block.reference_videos?.max ?? 0) >= Math.max(1, filled.videos);
    case "audios":
      return (block.reference_audios?.max ?? 0) >= Math.max(1, filled.audios);
    case "elements":
      return (block.elements?.max ?? 0) >= Math.max(1, filled.elements);
    case "shots":
      return block.multi_shot !== undefined && block.multi_shot !== null;
  }
};

const isFilled = (input: VideoInput, filled: Filled): boolean =>
  COUNTED.includes(input) ? filled[input as Counted] > 0 : filled[input as "start"];

/** The model's blocks for the types the composer may send, in live's order. */
function modesOf(
  model: CatalogImageModel | CatalogVideoModel,
  offered: readonly GenerationType[],
): [GenerationType, VideoMode][] {
  if (model.medium !== "video") return [];
  return VIDEO_TYPES.flatMap((type) => {
    const block = model.modes[type];
    return block === undefined || !offered.includes(type) ? [] : [[type, block]];
  });
}

/** The modes left once the filled inputs narrow them. */
function activeModes(
  model: CatalogImageModel | CatalogVideoModel,
  filled: Filled,
  offered: readonly GenerationType[],
): [GenerationType, VideoMode][] {
  return ORDER.reduce((active, input) => {
    if (!isFilled(input, filled)) return active;
    const narrowed = active.filter(([, block]) => taken(block, input, filled));
    return narrowed.length > 0 ? narrowed : active;
  }, modesOf(model, offered));
}

/**
 * The blocks the footer's options come from (live's `collectionsForOptions`, issue 366): a tool's
 * type alone; text to video alone while nothing is filled, as live's form starts; else the modes
 * the filled inputs leave.
 */
export function optionModes(
  model: CatalogVideoModel,
  filled: Filled,
  offered: readonly GenerationType[],
  type: GenerationType,
): VideoMode[] {
  const own = model.modes[type];
  if (!VIDEO_TYPES.includes(type)) return own === undefined ? [] : [own];
  const nothing =
    !filled.start &&
    !filled.end &&
    filled.middle === 0 &&
    filled.references === 0 &&
    filled.elements === 0 &&
    !filled.shots;
  const text = offered.includes("video.text") ? model.modes["video.text"] : undefined;
  if (nothing && text !== undefined) return [text];
  return activeModes(model, filled, offered).map(([, block]) => block);
}

/** The job's type: the first mode left, or undefined where the model has none on offer. */
export function videoTypeOf(
  model: CatalogImageModel | CatalogVideoModel,
  filled: Filled,
  offered: readonly GenerationType[],
): GenerationType | undefined {
  return activeModes(model, filled, offered)[0]?.[0];
}

export type InputState = "hidden" | "disabled" | "enabled";

/** Whether an input shows, and whether the modes left take it. */
export function inputState(
  model: CatalogImageModel | CatalogVideoModel,
  filled: Filled,
  offered: readonly GenerationType[],
  input: VideoInput,
): InputState {
  const all = modesOf(model, offered);
  const any = { ...filled, middle: 0, references: 0, videos: 0, audios: 0, elements: 0 };
  if (!all.some(([, block]) => taken(block, input, any))) return "hidden";
  const active = activeModes(model, filled, offered);
  return active.some(([, block]) => taken(block, input, any))
    ? "enabled"
    : "disabled";
}

/** The most references of a medium any of the model's modes takes (live's `getMaxReferenceCount`). */
export function referenceMax(
  model: CatalogImageModel | CatalogVideoModel,
  offered: readonly GenerationType[],
  field: "reference_images" | "reference_videos" | "reference_audios",
): number {
  return Math.max(0, ...modesOf(model, offered).map(([, block]) => block[field]?.max ?? 0));
}

/** The most reference images any of the model's modes takes. */
export function referenceImagesMax(
  model: CatalogImageModel | CatalogVideoModel,
  offered: readonly GenerationType[],
): number {
  return referenceMax(model, offered, "reference_images");
}

/** The most keyframes any of the model's modes takes between its frames (Kling 4.0's 8). */
export function middleFramesMax(
  model: CatalogImageModel | CatalogVideoModel,
  offered: readonly GenerationType[],
): number {
  return Math.max(0, ...modesOf(model, offered).map(([, block]) => block.middle_frames?.max ?? 0));
}
